// components/DebouncedAsyncSelect.js
import React, { useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash/debounce';

/**
 * A reusable debounced AsyncSelect.
 * - Accepts custom loadOptions, placeholder, noOptionsMessage, and onItemSelect
 * - Debounces API call by 300ms
 * - Highlights matched text in dropdown
 * - Doesn't highlight the selected value
 * - Prevents visual distinction of previously selected item in dropdown
 */
const DebouncedAsyncSelect = ({
  loadOptions,
  onItemSelect,
  placeholder = "請輸入關鍵字",
  noOptionsMessage = () => "找不到符合的項目",
  multi = false,
  isClearable = true,
}) => {
  // Debounce it using useMemo so it doesn’t recreate every render
  const debouncedLoadOptions = useMemo(
    () =>
      debounce((inputValue, callback) => {
        loadOptions(inputValue).then(callback);
      }, 300),
    [loadOptions]
  );

  // Custom highlight of matched text in dropdown
  const formatOptionLabel = ({ label }, { inputValue, context }) => {
    if (context === "value") return label;

    const regex = new RegExp(`(${inputValue})`, 'gi');
    const parts = label.split(regex);

    return (
      <div>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <strong key={index} style={{ backgroundColor: "#ffe58f" }}>{part}</strong>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </div>
    );
  };

  const customStyles = {
    container: (base) => ({ ...base, marginBottom: "1rem" }),
    menuList: (base) => ({
      ...base,
      maxHeight: "250px",
    }),
    control: (provided) => ({ ...provided }),
    singleValue: (provided) => ({ ...provided }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#deebff" : "white",
      color: "#333",
    }),
  };

  return (
    <AsyncSelect
      cacheOptions
      loadOptions={debouncedLoadOptions}
      formatOptionLabel={formatOptionLabel}
      defaultOptions={false}
      onChange={(selectedOption) => {
        if (selectedOption?.value) {
          onItemSelect(selectedOption);
        }
      }}
      placeholder={placeholder}
      noOptionsMessage={noOptionsMessage}
      styles={customStyles}
      isMulti={multi}
      isClearable={isClearable}
    />
  );
};

export default DebouncedAsyncSelect;
